import { useState, useMemo } from "react";
import { Input } from "@tellescope/design-system";
import { Card, CardContent } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import type { ManagedContentRecord } from "@tellescope/types-models";
import type { ContentListBlock as ContentListBlockType } from "../../../../shared/schema";
import { MobileList } from "../../MobileList";
import { useEnduserContent } from "../../../hooks/useEnduserContent";
import { useContentPreview } from "../../../hooks/useContentPreview";
import { useContentFilter } from "../../../hooks/useItemFilter";
import { ContentPreviewDialog } from "../../ContentPreviewDialog";
import { ContentCard } from "./ContentCard";
import { ContentListSkeleton } from "./ContentListSkeleton";

interface ContentListBlockProps {
  block: ContentListBlockType;
}

export const ContentListBlock = ({ block }: ContentListBlockProps) => {
  const { t } = useTranslation("content");
  const { itemFilter, showSearch } = block.props;
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { content: rawContent, isLoading: contentLoading, error } = useEnduserContent();

  const { filteredItems: content, isLoading: filterLoading } = useContentFilter({
    items: rawContent,
    itemFilter,
  });

  const isLoading = contentLoading || filterLoading;

  const searchedContent = useMemo(() => {
    if (!showSearch || !searchQuery.trim()) return content;
    const query = searchQuery.toLowerCase();
    return content.filter(
      (item) =>
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
    );
  }, [content, showSearch, searchQuery]);

  const {
    loadAttachmentUrl,
    attachmentUrl,
    previewContent,
    isLoading: isLoadingPreview,
    clearPreview,
  } = useContentPreview({
    onError: (err) => {
      console.error("Content preview failed:", err);
      setLoadingId(null);
    },
  });

  const handleSelectContent = async (selectedContent: ManagedContentRecord) => {
    setLoadingId(selectedContent.id);
    try {
      await loadAttachmentUrl(selectedContent);
    } finally {
      setLoadingId(null);
    }
  };

  const handleClosePreview = () => {
    clearPreview();
    setLoadingId(null);
  };

  if (isLoading) {
    return <ContentListSkeleton count={Math.min(block.props.maxItems ?? 10, 5)} />;
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load content")}</CardContent>
      </Card>
    );
  }

  return (
    <>
      {showSearch && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("Search content...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}
      <MobileList
        items={searchedContent}
        renderItem={(item) => (
          <ContentCard
            content={item}
            onSelect={handleSelectContent}
            isLoading={isLoadingPreview}
            loadingId={loadingId}
            headerPhotoStyle={block.props.headerPhotoStyle ?? "banner"}
          />
        )}
        title={block.props.title ?? t("Your Content")}
        maxItems={block.props.maxItems ?? 10}
        emptyMessage={block.props.emptyMessage ?? t("No content available")}
      />

      <ContentPreviewDialog
        content={previewContent}
        attachmentUrl={attachmentUrl}
        isLoading={isLoadingPreview}
        onClose={handleClosePreview}
      />
    </>
  );
};
